import { PasswordInput } from '@inflearn/ds-react-next';
import type { MantineSize } from '@mantine/core';
import _ from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { Controller, type FieldErrors, type Path, useFormContext } from 'react-hook-form';

import { useState } from 'react';
import { usePasswordValidation } from '../../../../../hooks/usePasswordValidation';
import ValidationMessage from '../../../../common/ValidationMessage';
import { ValidationPopover } from '../../../../common/ValidationPopover/ValidationPopover';

export interface PasswordFormValue {
  password: string;
  passwordConfirm: string;
}

export type PasswordVerifyFormItemsProps<T> = T extends PasswordFormValue
  ? {
      errors: FieldErrors<T>;
      size?: MantineSize;
      showLabel?: boolean;
      passwordPlaceholder?: string;
      passwordConfirmPlaceholder?: string;
    }
  : never;

export const PasswordVerifyFormItems = <T extends PasswordFormValue>({
  errors,
  size = 'md',
  showLabel = true,
  passwordPlaceholder,
  passwordConfirmPlaceholder,
}: PasswordVerifyFormItemsProps<T>) => {
  const { t } = useTranslation();
  const { onValidate, validationMessages } = usePasswordValidation();
  const [isFocus, setFocus] = useState(false);
  const { control } = useFormContext<T>();

  return (
    <>
      <Controller
        name={'password' as Path<T>}
        control={control}
        render={({ field }) => (
          <ValidationPopover opened={isFocus} validationMessages={validationMessages}>
            <span>
              <PasswordInput
                value={field.value}
                onChange={(e) => {
                  /** 공백 제외 */
                  e.target.value = e.target.value.replace(/\s/g, '');
                  field.onChange(e);
                  onValidate(e.target.value);
                }}
                size={size}
                label={showLabel ? t('비밀번호') : undefined}
                placeholder={passwordPlaceholder ?? t('auth.password.placeholder')}
                aria-label={passwordPlaceholder ?? t('비밀번호')}
                error={!_.isUndefined(errors.password?.message)}
                onFocus={() => setFocus(true)}
                onBlur={() => {
                  setFocus(false);
                  field.onBlur();
                }}
              />
            </span>
          </ValidationPopover>
        )}
      />
      <Controller
        name={'passwordConfirm' as Path<T>}
        control={control}
        render={({ field }) => (
          <PasswordInput
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
            size={size}
            label={showLabel ? t('비밀번호 확인') : undefined}
            placeholder={passwordConfirmPlaceholder ?? t('auth.password.placeholder')}
            aria-label={passwordConfirmPlaceholder ?? t('비밀번호 확인')}
            error={
              errors.passwordConfirm?.message && (
                <ValidationMessage message={errors.passwordConfirm.message} />
              )
            }
          />
        )}
      />
    </>
  );
};
